/**
 * Snakes and Ladders Game Engine
 *
 * Core game logic: board setup, dice rolling, token movement,
 * snake/ladder detection, bounce-back from 100, extra turn on 6,
 * win condition, turn management, serialization.
 */

import {
  SnakeOrLadder,
  PlayerColor,
  PlayerType,
  PlayerState,
  MoveResult,
  SnakesLaddersGameState,
  SnakesLaddersGameSettings,
  SNAKES,
  LADDERS,
  PLAYER_COLORS,
  TOTAL_SQUARES,
  MAX_CONSECUTIVE_SIXES,
} from './types';

export class SnakesLaddersEngine {
  private state: SnakesLaddersGameState;
  private snakes: SnakeOrLadder[];
  private ladders: SnakeOrLadder[];

  constructor() {
    this.snakes = [...SNAKES];
    this.ladders = [...LADDERS];
    this.state = this.createEmptyState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  /**
   * Initialize a new game with the given settings.
   */
  initializeGame(settings: SnakesLaddersGameSettings): void {
    const players: PlayerState[] = [];

    if (settings.mode === 'singleplayer') {
      // Human player
      players.push({
        id: 'player-1',
        color: PLAYER_COLORS[0],
        position: 0,
        type: 'human',
      });
      // AI opponents
      const aiCount = settings.aiOpponentCount || 1;
      for (let i = 0; i < aiCount; i++) {
        players.push({
          id: `ai-${i + 1}`,
          color: PLAYER_COLORS[i + 1],
          position: 0,
          type: 'ai',
          aiDifficulty: settings.aiDifficulty || 'medium',
        });
      }
    } else {
      // Local or online multiplayer — all human
      for (let i = 0; i < settings.playerCount; i++) {
        players.push({
          id: `player-${i + 1}`,
          color: PLAYER_COLORS[i],
          position: 0,
          type: 'human',
        });
      }
    }

    this.state = {
      players,
      currentPlayerIndex: 0,
      moveHistory: [],
      gameStatus: 'active',
      consecutiveSixes: 0,
      extraTurnEnabled: settings.extraTurnOnSix,
      gameId: `sl-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
    };
  }

  // =========================================================================
  // Dice
  // =========================================================================

  /** Roll a random dice value 1–6 */
  rollDice(): number {
    return Math.floor(Math.random() * 6) + 1;
  }

  /** Set dice value externally (for multiplayer server-provided rolls) */
  setDiceValue(_value: number): number {
    // Validate
    const clamped = Math.max(1, Math.min(6, Math.round(_value)));
    return clamped;
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  /**
   * Execute a move for the current player with the given dice value.
   * Returns the MoveResult describing what happened.
   */
  executeMove(diceValue: number): MoveResult {
    if (this.state.gameStatus !== 'active') {
      throw new Error('Game is not active');
    }

    const player = this.state.players[this.state.currentPlayerIndex];
    const fromPosition = player.position;
    let toPosition = fromPosition + diceValue;
    let finalPosition = toPosition;
    let bouncedBack = false;
    let snakeOrLadder: SnakeOrLadder | undefined;
    let won = false;
    let forfeitedTurn = false;

    // Check for three consecutive sixes
    if (diceValue === 6 && this.state.extraTurnEnabled) {
      const newConsecutive = this.state.consecutiveSixes + 1;
      if (newConsecutive >= MAX_CONSECUTIVE_SIXES) {
        // Forfeit turn — do NOT execute the move
        forfeitedTurn = true;
        const result: MoveResult = {
          player: player.color,
          diceValue,
          fromPosition,
          toPosition: fromPosition,
          finalPosition: fromPosition,
          bouncedBack: false,
          extraTurn: false,
          won: false,
          forfeitedTurn: true,
          timestamp: Date.now(),
        };
        this.state.moveHistory.push(result);
        this.state.consecutiveSixes = 0;
        return result;
      }
    }

    // Bounce-back rule: if position + dice > 100, bounce back from 100
    if (toPosition > TOTAL_SQUARES) {
      toPosition = TOTAL_SQUARES - (toPosition - TOTAL_SQUARES);
      bouncedBack = true;
    }

    finalPosition = toPosition;

    // Check for snake or ladder at landing position (no chaining)
    const entity = this.getSnakeOrLadderAt(toPosition);
    if (entity) {
      finalPosition = entity.end;
      snakeOrLadder = entity;
    }

    // Check for win
    if (finalPosition === TOTAL_SQUARES) {
      won = true;
    }

    // Update player position
    player.position = finalPosition;

    // Determine extra turn
    const extraTurn = diceValue === 6 && this.state.extraTurnEnabled && !won;

    // Update consecutive sixes counter
    if (diceValue === 6 && this.state.extraTurnEnabled) {
      this.state.consecutiveSixes++;
    } else {
      this.state.consecutiveSixes = 0;
    }

    // Record move
    const result: MoveResult = {
      player: player.color,
      diceValue,
      fromPosition,
      toPosition,
      finalPosition,
      snakeOrLadder,
      bouncedBack,
      extraTurn,
      won,
      forfeitedTurn: false,
      timestamp: Date.now(),
    };
    this.state.moveHistory.push(result);

    // Handle win
    if (won) {
      this.state.gameStatus = 'completed';
      this.state.winner = player.color;
    }

    return result;
  }

  // =========================================================================
  // Turn Management
  // =========================================================================

  /**
   * Advance to the next player's turn.
   * Call this after executeMove() unless the player has an extra turn.
   */
  advanceTurn(): void {
    if (this.state.gameStatus !== 'active') return;

    const lastMove = this.state.moveHistory[this.state.moveHistory.length - 1];

    // If the last move grants an extra turn, don't advance
    if (lastMove && lastMove.extraTurn && !lastMove.forfeitedTurn) {
      return;
    }

    // Reset consecutive sixes when advancing to next player
    this.state.consecutiveSixes = 0;
    this.state.currentPlayerIndex =
      (this.state.currentPlayerIndex + 1) % this.state.players.length;
  }

  /** Check if the current player has an extra turn (rolled a 6) */
  hasExtraTurn(): boolean {
    const lastMove = this.state.moveHistory[this.state.moveHistory.length - 1];
    if (!lastMove) return false;
    return lastMove.extraTurn && !lastMove.forfeitedTurn;
  }

  /** Get the current consecutive sixes count */
  getConsecutiveSixes(): number {
    return this.state.consecutiveSixes;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getGameState(): SnakesLaddersGameState {
    return { ...this.state, players: this.state.players.map(p => ({ ...p })) };
  }

  getCurrentPlayer(): PlayerState {
    return { ...this.state.players[this.state.currentPlayerIndex] };
  }

  isGameOver(): boolean {
    return this.state.gameStatus === 'completed';
  }

  getWinner(): PlayerColor | undefined {
    return this.state.winner;
  }

  getMoveHistory(): MoveResult[] {
    return [...this.state.moveHistory];
  }

  // =========================================================================
  // Board Queries
  // =========================================================================

  /** Check if a position has a snake or ladder. Returns the entity or undefined. */
  getSnakeOrLadderAt(position: number): SnakeOrLadder | undefined {
    const allEntities = [...this.snakes, ...this.ladders];
    return allEntities.find(e => e.start === position);
  }

  /** Get the board layout (snakes and ladders arrays) */
  getBoardLayout(): { snakes: SnakeOrLadder[]; ladders: SnakeOrLadder[] } {
    return { snakes: [...this.snakes], ladders: [...this.ladders] };
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  /** Serialize the complete game state to JSON string */
  serialize(): string {
    return JSON.stringify(this.state);
  }

  /** Deserialize a JSON string to game state. Returns the state or an error. */
  deserialize(json: string): SnakesLaddersGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);

      // Basic validation
      if (!parsed.players || !Array.isArray(parsed.players)) {
        return { error: 'Invalid state: missing players array' };
      }
      if (typeof parsed.currentPlayerIndex !== 'number') {
        return { error: 'Invalid state: missing currentPlayerIndex' };
      }
      if (!Array.isArray(parsed.moveHistory)) {
        return { error: 'Invalid state: missing moveHistory' };
      }
      if (!['active', 'completed'].includes(parsed.gameStatus)) {
        return { error: 'Invalid state: invalid gameStatus' };
      }

      // Validate player positions
      for (const player of parsed.players) {
        if (typeof player.position !== 'number' || player.position < 0 || player.position > TOTAL_SQUARES) {
          return { error: `Invalid state: player position out of range (${player.position})` };
        }
      }

      return parsed as SnakesLaddersGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  /** Set the game state directly (for state restoration) */
  setGameState(state: SnakesLaddersGameState): void {
    this.state = {
      ...state,
      players: state.players.map(p => ({ ...p })),
      moveHistory: state.moveHistory.map(m => ({ ...m })),
    };
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private createEmptyState(): SnakesLaddersGameState {
    return {
      players: [],
      currentPlayerIndex: 0,
      moveHistory: [],
      gameStatus: 'active',
      consecutiveSixes: 0,
      extraTurnEnabled: true,
      gameId: '',
      startedAt: 0,
    };
  }
}
